<template>
  <div class="hire-action-column">
    <WdsTooltip v-if="isCancelled" :global="tableGlobals" left>
      <template #tooltip-content>
        {{ $t('No actions available - this hire has been cancelled by the owner.') }}
      </template>

      <WdsButtonGhost disabled>
        <WdsIcon name="overflow-menu-horizontal" :size="16" />
      </WdsButtonGhost>
    </WdsTooltip>
    <WdsMenu
      v-else
      z-index="50"
      placement="bottom-end"
      manual-trigger
      :global="tableGlobals"
      @menu:open="load"
    >
      <template #trigger="{ toggle }">
        <WdsButtonGhost
          :data-test="`contract-table-contract-row-actions-${input.id}`"
          @click="toggle"
        >
          <WdsIcon name="overflow-menu-horizontal" :size="16" />
        </WdsButtonGhost>
      </template>

      <div v-if="column && !loading">
        <WdsMenuOption
          :href="
            laravelNamedRoute(namedRoutes.hires.hire.view, { hire: column.id, tab: 'overview' })
          "
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.DIRECT_HIRE" />
          {{ $t('View hire details') }}
        </WdsMenuOption>

        <WdsMenuDivider v-if="!isDraft" />

        <WdsMenuOption
          v-if="isTerminated || isEnded"
          :href="
            laravelNamedRoute('frontend.user.profile.hiredirect', { freelancer: column.worker.id })
          "
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.RENEW" />
          {{ $t('Re-hire') }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="(isActive || isSigned || isOffered) && column.conversation?.id"
          :href="
            laravelNamedRoute(namedRoutes.hires.hire.view, { hire: column.id, tab: 'overview' }) +
            '?manage-contract=1'
          "
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.EDIT" />
          {{ $t('Manage contract') }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="
            (isActive || isSigned || isOffered || isTerminated || isEnded) &&
            column.conversation?.id
          "
          :href="laravelNamedRoute('messages.show', { id: column.conversation.id })"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.CHAT" />
          {{
            column.recruiter && column.recruiterManagesWorkers
              ? $t('Message staffing agency')
              : $t('Message worker')
          }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="!isDraft"
          :href="laravelNamedRoute('yjob.contract', { hire: column.id })"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.DOCUMENT_DOWNLOAD" />
          {{ $t('Download contract PDF') }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="(isActive || isEnded) && column.classification?.id"
          :href="laravelNamedRoute('sds.pdf', { sds: column.classification.id })"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.DOCUMENT_DOWNLOAD" />
          {{ $t('Download status determination statement') }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="
            (isTerminated || isEnded) &&
            column.conversation?.id &&
            column.payReg?.paginatorInfo.total > 0
          "
          :href="
            laravelNamedRoute('messages.show', { id: column.conversation.id }) +
            '?show_feedback_modal=1'
          "
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.STAR" />
          {{ $t('Review work') }}
        </WdsMenuOption>

        <div v-if="column.canRemindWorkerForBilling && (isActive || isInactive)">
          <WdsMenuDivider />
          <WdsMenuOption
            :data-test="'bulk-remind-to-submit-bill-button-' + column.id"
            @click="toggleRemindToBillModal"
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.RECEIPT" />
            {{ $t('Remind to submit bill') }}
          </WdsMenuOption>
        </div>
        <WdsMenuOption
          v-if="column.job.isJobPost"
          :href="laravelNamedRoute(namedRoutes.jobs.show, { job: column.job.id })"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.DOCUMENT_INFO" />
          {{ $t('View job post') }}
        </WdsMenuOption>
        <WdsMenuOption
          v-if="column.company.hasActiveWebhooks && !isDraft"
          :data-swaltext="$t('Are you sure you want to trigger Hire Accepted webhook?')"
          :data-swalconfirmtext="$t('Yes')"
          :data-swalcompletedtext="$t('Webhook triggered.')"
          :data-formid="`form-trigger-webhook-${column.number}`"
          href="#"
          class="swal-delete"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.DIRECT_HIRE" />
          {{ $t('Trigger Webhook') }}
        </WdsMenuOption>

        <WdsMenuDivider
          v-if="isDraft || isSigned || isOffered || (isActive && column.canTerminateContract)"
        />
        <WdsMenuOption
          v-if="(isDraft || isSigned || isOffered) && user.hasPermission('manage-contracts')"
          :href="
            laravelNamedRoute(namedRoutes.hires.hire.view, {
              hire: column.id,
              tab: 'overview',
              'cancel-hire': 1,
            })
          "
          theme="DESTRUCTIVE"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.CLOSE_OUTLINE" />
          {{ $t('Cancel hire') }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="isActive && column.canTerminateContract && user.hasPermission('manage-contracts')"
          :href="
            laravelNamedRoute(namedRoutes.hires.hire.view, {
              hire: column.id,
              tab: 'overview',
              'terminate-contract': 1,
            })
          "
          theme="DESTRUCTIVE"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.CLOSE_OUTLINE" />
          {{ $t('Terminate contract') }}
        </WdsMenuOption>
      </div>
      <div
        v-else-if="loading"
        class="tw-px-4 tw-py-2"
        :data-test="'contract-table-contract-row-actions-loading-' + input.id"
      >
        <WdsSpinner indeterminate />
      </div>
      <div
        v-else
        class="tw-px-4 tw-py-2"
        :data-test="'contract-table-contract-row-actions-loading-' + input.id"
      >
        <WdsText :text="$t('No actions found available for this hire')" />
      </div>
    </WdsMenu>
    <BaseHtmlForm
      v-if="!!column?.job?.number"
      :id="`form-job-close-${column.job.number}`"
      :action="`/job/${column.job.number}/close`"
      method="PATCH"
      hidden
    />
    <BaseHtmlForm
      v-if="!!column?.number"
      :id="`form-trigger-webhook-${column.number}`"
      :action="`/integrations/webhook/hireAccepted/trigger/${column.number}`"
      method="POST"
      hidden
      append-user-and-account
    />
  </div>
  <teleport :to="tableGlobals">
    <ModalRemindToBill
      v-if="remindToBillModalIsOpen && column"
      :hire-ids="[column.number]"
      :job-name="column.job.name"
      :freelancer-name="column.worker.name"
      :is-open="remindToBillModalIsOpen"
      :default-message="column.company?.settings?.defaultMessageRemindWorkerToBill ?? undefined"
      class="text-left f-14"
      @reminder-sent="toggleRemindToBillModal"
      @closed="toggleRemindToBillModal"
    />
  </teleport>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsSpinner,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { FragmentType, graphql, useFragment } from '@/gql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { HireActiveStatus } from '@/gql/graphql'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import ModalRemindToBill from '@/components/ModalRemindToBill.vue'
import { user } from '@/composables/useUser'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const HireActionDataRequest = graphql(`
  fragment HireActionDataRequest on Hire {
    id
    number
    activeStatus
    canRemindWorkerForBilling
    company {
      id
      hasActiveWebhooks
      settings {
        defaultMessageRemindWorkerToBill
      }
    }
    canRemindWorkerForBilling
    canCancelContract
    canTerminateContract
    recruiter {
      id
    }
    recruiterManagesWorkers
    conversation {
      id
    }
    classification {
      id
    }
    job {
      id
      name
      number
      isJobPost
    }
    payReg: paymentRequests(first: 1, page: 1) {
      paginatorInfo {
        total
      }
    }
    worker {
      id
      name
    }
  }
`)

const HireActionColumn = graphql(`
  fragment HireActionColumn on Hire {
    id
    number # This is used for tables bulk actions
    activeStatus # used to check if we are disabled
  }
`)

const props = defineProps<{ row: FragmentType<typeof HireActionColumn> }>()

const input = computed(() => useFragment(HireActionColumn, props.row))
const { result, loading, load } = useLazyQuery(
  graphql(`
    query HirerActionColumnMenu($id: ID!) {
      hire(id: $id) {
        ...HireActionDataRequest
      }
    }
  `),
  () => ({ id: input.value.id }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const column = computed(() => useFragment(HireActionDataRequest, result.value?.hire))

const remindToBillModalIsOpen = ref(false)

const toggleRemindToBillModal = () => {
  remindToBillModalIsOpen.value = !remindToBillModalIsOpen.value
}

const isDraft = computed(() => input.value?.activeStatus === HireActiveStatus.Draft)
const isOffered = computed(() => input.value?.activeStatus === HireActiveStatus.Offered)
const isSigned = computed(() => input.value?.activeStatus === HireActiveStatus.Ready)
const isActive = computed(() => input.value?.activeStatus === HireActiveStatus.Active)
const isCancelled = computed(() => input.value?.activeStatus === HireActiveStatus.Cancelled)
const isTerminated = computed(() => input.value?.activeStatus === HireActiveStatus.Terminated)
const isEnded = computed(() => input.value?.activeStatus === HireActiveStatus.Ended)

const isInactive = computed(() =>
  column.value
    ? [HireActiveStatus.Ended, HireActiveStatus.Cancelled, HireActiveStatus.Terminated].includes(
        column.value.activeStatus,
      )
    : undefined,
)
</script>

<style scoped lang="scss">
.hire-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
